// @flow
import styles from './SaveJob.less';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { pick, isEmpty } from 'lodash';

import SignInRegisterLink, {
  LOGIN_MODES
} from 'src/components/SignInRegisterLink/SignInRegisterLink';
import {
  StarIcon,
  TickCircleIcon,
  ScreenReaderOnly
} from 'seek-style-guide/react';
import {
  saveJob,
  removeSavedJob,
  signedOutSaveJobClicked
} from 'src/store/savedJobs/savedJobs';
import NavLink from 'src/components/NavLink/NavLink';
import { getJobStatusFlags } from 'src/modules/job-status';

const filterInheritedProps = props =>
  pick(props, ['component', 'color', 'fullWidth']);

const mapStateToProps = ({ user, savedJobs, location }) => ({
  savedJobs: savedJobs.jobs,
  authenticated: user.authenticated,
  location
});

const mapDispatchToProps = dispatch => ({
  saveJobHandler: job => dispatch(saveJob(job)),
  removeSavedJobHandler: job => dispatch(removeSavedJob(job)),
  signedOutSaveJobHandler: signedOutSaveJobClicked
});

type Props = {
  job: ChaliceJob,
  location: UrlLocation,
  savedJobs: SavedJobs,
  saveJobHandler: Function,
  removeSavedJobHandler: Function,
  signedOutSaveJobHandler: Function,
  authenticated: boolean,
  withAppliedTick: boolean,
  buttonCopy: {
    saved?: string,
    unsaved?: string,
    applied?: string
  },
  className?: string
};

class SaveJob extends Component<Props> {
  static defaultProps = {
    withAppliedTick: false,
    className: ''
  };

  renderApplied() {
    const { className, buttonCopy } = this.props;
    const title = 'You have applied for this job';

    return (
      <div
        className={classnames(styles.root, className)}
        title={title}
        data-automation="applied-job"
      >
        <TickCircleIcon
          className={styles.icon}
          svgClassName={`${styles.iconSvg} ${styles.iconSvg_isApplied}`}
        />
        <ScreenReaderOnly>{title}</ScreenReaderOnly>
        {buttonCopy && buttonCopy.applied && (
          <span aria-hidden="true">{buttonCopy.applied}</span>
        )}
      </div>
    );
  }

  renderSave(rootClasses) {
    const {
      buttonCopy,
      job,
      removeSavedJobHandler,
      savedJobs,
      saveJobHandler,
      ...props
    } = this.props;

    const { id } = job;

    const { isSaved } = getJobStatusFlags({ savedJobs, jobId: id });
    const buttonTitle = isSaved
      ? 'Remove from your saved jobs'
      : 'Save this job';
    const clickHandler = isSaved ? removeSavedJobHandler : saveJobHandler;

    const svgClassName = classnames({
      [styles.iconSvg]: true,
      [styles.iconSvg_isSaved]: isSaved
    });

    const { saved = '', unsaved = '' } = buttonCopy || {};
    const buttonText = isSaved ? saved : unsaved;

    /* eslint-disable react/jsx-no-bind */
    return (
      <span>
        {isSaved && <ScreenReaderOnly>This is a saved job.</ScreenReaderOnly>}
        <NavLink
          className={rootClasses}
          onClick={() => clickHandler(job)}
          title={buttonTitle}
          aria-label={buttonTitle}
          rel="nofollow"
          role="button"
          data-automation={isSaved ? 'remove-saved-job' : 'save-job'}
          {...filterInheritedProps(props)}
        >
          <StarIcon
            filled={isSaved}
            className={styles.icon}
            svgClassName={svgClassName}
          />
          {!isEmpty(buttonCopy) && <span>{buttonText}</span>}
        </NavLink>
      </span>
    );
    /* eslint-enable react/jsx-no-bind */
  }

  renderSignedIn(rootClasses) {
    const {
      job: { id },
      savedJobs,
      withAppliedTick
    } = this.props;
    const applied = savedJobs[id] === 'applied';

    return applied && withAppliedTick
      ? this.renderApplied()
      : this.renderSave(rootClasses);
  }

  renderSignedOut(rootClasses) {
    const {
      buttonCopy,
      job: { id },
      location: { pathname, query },
      signedOutSaveJobHandler,
      ...props
    } = this.props;

    return (
      <SignInRegisterLink
        mode={LOGIN_MODES.SIGN_IN}
        returnLocation={{
          pathname,
          query: {
            ...query,
            savejob: id
          }
        }}
        rel="nofollow"
        className={rootClasses}
        title="Sign in to save this job"
        data-automation="signed-out-save-job"
        preNavigationHooks={[signedOutSaveJobHandler]}
        {...filterInheritedProps(props)}
      >
        <StarIcon className={styles.icon} svgClassName={styles.iconSvg} />
        {!isEmpty(buttonCopy) && <span>{buttonCopy.unsaved}</span>}
      </SignInRegisterLink>
    );
  }

  render() {
    const { authenticated, className } = this.props;

    const rootClasses = classnames(styles.root, styles.root_isLink, className);

    return authenticated
      ? this.renderSignedIn(rootClasses)
      : this.renderSignedOut(rootClasses);
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SaveJob);
